import {
  CarWheelMeasurementType,
  CarWheelMeasurementTypeLabels,
  CarWheelPositionTextLabels,
  InspectionItemKind,
  TireSeason,
  type InspectionItemRecord
} from '@navio/nsdk';
import {
  CAR_WHEEL_ALL_SEASON_VALUE,
  CAR_WHEEL_SUMMER_VALUE,
  CAR_WHEEL_WINTER_VALUE,
  type CarWheelSeasonValue
} from 'noddi-types';
import { CarSideIcon, CarWheelPositions, type CarWheelPositionType, type NoddiIconName } from 'noddi-ui-common';
import { NoddiCard } from 'noddi-ui-web';
import {
  commonTexts,
  formatDecimalUpTo,
  INSPECTION_WHEELS,
  MISSING_VALUE,
  WHEEL_LABELS,
  wheelFieldFromLocation,
  type TreadDepthThresholds,
  type WheelField
} from 'noddi-util';
import { TireDimensionsCard } from '../CarDetails/Cards/Inspection/TireDimensionsCard';
import { TireMeasurementCard } from '../CarDetails/Cards/Inspection/TireMeasurementCard';
import { NavioTireSeasonChip } from '../CarWheel/NavioTireSeasonChip';
import { TreadDepthMeter } from '../CarWheel/TreadDepthMeter';
import { InspectionCard } from './InspectionCard';
import { getInspectionCardTireDimensions } from './inspectionTireDimensions';
import type { WheelSetGroup } from './wheelSetGroups';

const SEASON_VALUE: Record<TireSeason, CarWheelSeasonValue> = {
  [TireSeason.SUMMER]: CAR_WHEEL_SUMMER_VALUE,
  [TireSeason.WINTER]: CAR_WHEEL_WINTER_VALUE,
  [TireSeason.ALL_SEASON]: CAR_WHEEL_ALL_SEASON_VALUE
};

const MEASUREMENT_ICON: Record<CarWheelMeasurementType, NoddiIconName> = {
  [CarWheelMeasurementType.TREAD_DEPTH]: 'Ruler',
  [CarWheelMeasurementType.AIR_PRESSURE]: 'Activity',
  [CarWheelMeasurementType.TPMS_BATTERY_LEVEL]: 'Info'
};

type Props = {
  group: WheelSetGroup;
  carId?: number;
  thresholds: TreadDepthThresholds | null;
};

/** One wheel set: the season's tread, its specification, and its photos. */
export const SeasonWheelSet = ({ group, carId, thresholds }: Props) => {
  const tread = group.items.filter(
    (item) =>
      item.kind === InspectionItemKind.MEASUREMENT && item.measurementType === CarWheelMeasurementType.TREAD_DEPTH
  );
  const otherMeasurements = group.items.filter(
    (item) =>
      item.kind === InspectionItemKind.MEASUREMENT && item.measurementType !== CarWheelMeasurementType.TREAD_DEPTH
  );
  const photos = group.items.filter((item) => item.kind === InspectionItemKind.PHOTO && item.imageUrl);
  const dimensions = getInspectionCardTireDimensions(group.items.filter((item) => item.tire?.specification));
  const hasDimensions = Boolean(dimensions.all || dimensions.front || dimensions.rear);
  const wheelPhotos = INSPECTION_WHEELS.flatMap(({ field, location }) => {
    const photo = photos.find((item) => item.location === location);
    if (!photo?.imageUrl) {
      return [];
    }
    return [{ position: field, label: WHEEL_LABELS[field], imageUrl: photo.imageUrl }];
  });
  const corners = treadCorners(tread);
  // Session rows leave the wheel set empty, so the dimensions card cannot filter by set id.
  // Passing the season keeps the summer size off the winter card.
  const showRecordedDimensions = !hasDimensions && carId != null;
  const measurementTypes = [
    ...new Set(otherMeasurements.map((item) => item.measurementType).filter((type) => type != null))
  ];

  return (
    <div className='flex flex-col gap-4'>
      {corners ? (
        <TireMeasurementCard
          tireSeason={group.season}
          thresholds={thresholds}
          measurements={corners}
          translations={{
            ...commonTexts.wheelMeasurements,
            leftFront: WHEEL_LABELS.frontLeft,
            rightFront: WHEEL_LABELS.frontRight,
            leftRear: WHEEL_LABELS.rearLeft,
            rightRear: WHEEL_LABELS.rearRight
          }}
        />
      ) : (
        tread.length > 0 && (
          <NoddiCard
            title={CarWheelMeasurementTypeLabels[CarWheelMeasurementType.TREAD_DEPTH]}
            size='small'
            icon='Ruler'
            iconTile
            headerNode={<NavioTireSeasonChip season={group.season} size='sm' />}
          >
            <div className='flex flex-col gap-4'>
              {tread.map((item) => (
                <TreadReading key={item.id} item={item} thresholds={thresholds} />
              ))}
            </div>
          </NoddiCard>
        )
      )}

      {measurementTypes.map((type) => (
        <NoddiCard
          key={type}
          title={CarWheelMeasurementTypeLabels[type]}
          size='small'
          icon={MEASUREMENT_ICON[type]}
          iconTile
          headerNode={<NavioTireSeasonChip season={group.season} size='sm' />}
        >
          <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
            {otherMeasurements
              .filter((item) => item.measurementType === type)
              .map((item) => (
                <Reading key={item.id} item={item} />
              ))}
          </div>
        </NoddiCard>
      ))}

      {(hasDimensions || wheelPhotos.length > 0) && (
        <InspectionCard season={group.season} tireDimensions={dimensions} wheels={wheelPhotos} />
      )}

      {showRecordedDimensions && carId != null && (
        <TireDimensionsCard carId={carId} tireSeasonType={SEASON_VALUE[group.season]} />
      )}
    </div>
  );
};

const TreadReading = ({
  item,
  thresholds
}: {
  item: InspectionItemRecord;
  thresholds: TreadDepthThresholds | null;
}) => {
  const position = wheelPosition(item.location);
  const label = locationLabel(item.location);
  if (item.value?.unit === 'mm') {
    return (
      <TreadDepthMeter
        label={label}
        valueMm={item.value.value}
        thresholds={thresholds}
        icon={position ? <CarSideIcon carSide={position} width={16} height={26} /> : undefined}
      />
    );
  }
  return <Reading item={item} />;
};

const Reading = ({ item }: { item: InspectionItemRecord }) => {
  const position = wheelPosition(item.location);
  const value = item.value ? `${formatDecimalUpTo(item.value.value, 1)} ${item.value.unit}` : MISSING_VALUE;
  return (
    <div className='flex items-start gap-2'>
      {position ? <CarSideIcon carSide={position} width={16} height={26} /> : null}
      <div className='flex flex-col gap-1'>
        <p className='text-sm'>{locationLabel(item.location)}</p>
        <p className='text-sm font-medium'>{value}</p>
      </div>
    </div>
  );
};

const cornerFrom = (item: InspectionItemRecord | undefined) => {
  if (!item?.value) {
    return null;
  }
  return {
    measuredAt: item.createdAt,
    value: { value: item.value.value, unit: item.value.unit }
  };
};

const treadCorners = (items: InspectionItemRecord[]) => {
  const byField = new Map<WheelField, InspectionItemRecord>();
  for (const item of [...items].sort((left, right) => left.id - right.id)) {
    const field = wheelFieldFromLocation(item.location);
    if (!field || !item.value) {
      continue;
    }
    const existing = byField.get(field);
    if (existing?.value && existing.value.value !== item.value.value) {
      return null;
    }
    byField.set(field, item);
  }

  const frontLeft = cornerFrom(byField.get('frontLeft'));
  const frontRight = cornerFrom(byField.get('frontRight'));
  const rearLeft = cornerFrom(byField.get('rearLeft'));
  const rearRight = cornerFrom(byField.get('rearRight'));
  if (!frontLeft || !frontRight || !rearLeft || !rearRight) {
    return null;
  }

  return { frontLeft, frontRight, rearLeft, rearRight };
};

const wheelPosition = (location: InspectionItemRecord['location']): CarWheelPositionType | undefined => {
  if (!location || !(location in CarWheelPositions)) {
    return undefined;
  }
  return location as CarWheelPositionType;
};

const locationLabel = (location: InspectionItemRecord['location']): string => {
  if (!location) {
    return 'Unspecified';
  }
  if (location in CarWheelPositionTextLabels) {
    return CarWheelPositionTextLabels[location as keyof typeof CarWheelPositionTextLabels];
  }
  return location;
};
